import olMap from 'ol/Map';
import * as olExtent from 'ol/extent';
import Feature from 'ol/Feature';
import { MapBrowserEvent } from 'ol';
import { GpxRouteApi, IUserGpxRouteDetail, IUserGpxRouteSidebarItem, IGpxFilterCriteria } from './GpxRouteApi';
import { GpxRouteStore } from './GpxRouteStore';
import { GpxFeatureLayer } from './GpxFeatureLayer';
import { GpxUploadHandler } from './GpxUploadHandler';
import { GpxPanel } from './GpxPanel';
import { GpxPanelState } from './GpxPanelState';
import { GpxIconFactory } from './GpxIconFactory';
import { GpxWaypointLayer } from './GpxWaypointLayer';
import { GpxWaypointPopover } from './GpxWaypointPopover';
import { GpxWaypointDialog } from './GpxWaypointDialog';
import { GpxMetadataDialog } from './GpxMetadataDialog';
import { GpxEditSession } from './GpxEditSession';
import { MapModeManager } from '../map/MapModeManager';
import { MapSlidePanel } from '../MapSidePanel';
import TrailwiseEventBus from '../TrailwiseEventBus';
import {
    normaliseColour,
    extentFrom4326SidebarItem
} from './MapGpxUtils';

export class MapGpx {

    private readonly defaultColour: string = '#646463';
    private api: GpxRouteApi = new GpxRouteApi();
    private store: GpxRouteStore = new GpxRouteStore();
    private panelState: GpxPanelState = new GpxPanelState();
    private featureLayer: GpxFeatureLayer;
    private uploadHandler: GpxUploadHandler;
    private sidebar: GpxPanel;
    private iconFactory: GpxIconFactory = new GpxIconFactory();
    private waypointLayer: GpxWaypointLayer;
    private waypointPopover: GpxWaypointPopover;
    private editSession: GpxEditSession | null = null;
    private highlight: { gpxGuid: string; message: string } | null = null;

    public constructor(
        private map: olMap,
        private modeManager: MapModeManager | null = null,
        private slidePanel: MapSlidePanel | null = null,
        private canEditGpx: boolean = false
    ) {
        // A shared-route deep link clears the persisted search/tag filter so the
        // shared route is always shown — resolved before the layers/panel are
        // built so their first load is already unfiltered (no flash + re-query).
        this.highlight = this.resolveHighlightData();
        if (this.highlight) {
            this.panelState.update({ search: '', tags: [], page: 1 });
        }

        this.sidebar = new GpxPanel(
            this.store,
            this.api,
            this.defaultColour,
            this.panelState,
            {
                zoomToRoute: this.zoomToRoute,
                setRouteVisibility: this.setRouteVisibility,
                refreshMapRoutes: () => this.featureLayer.refresh(),
                onFilterCriteriaChanged: this.onFilterCriteriaChanged
            }
        );

        this.featureLayer = new GpxFeatureLayer(
            this.map,
            this.store,
            this.defaultColour,
            this.sidebar.buildGpxUi,
            this.getFilterCriteria
        );

        this.uploadHandler = new GpxUploadHandler(
            this.map,
            this.api,
            this.defaultColour,
            this.onRouteCreated,
            (msg) => this.sidebar.showErrorModal(msg),
            this.openGpxPanel
        );

        this.waypointLayer = new GpxWaypointLayer(this.map, this.api, this.store, this.iconFactory, this.getFilterCriteria);
        this.waypointPopover = new GpxWaypointPopover(this.map);

        TrailwiseEventBus.gpxRoutesChanged.on(() => {
            void this.waypointLayer.refresh();
        });

        this.initialiseEditing();

        void this.initialiseWaypointTypes();
        void this.initialiseRoutes();
    }

    /**
     * Wires the edit session (only when the composition root supplied the
     * mode manager and the editing UI exists on the page).
     */
    private initialiseEditing(): void {
        if (!this.modeManager || !document.getElementById('gpxEditToolbar')) {
            return;
        }

        this.editSession = new GpxEditSession(
            this.map,
            this.api,
            this.store,
            this.modeManager,
            this.featureLayer,
            this.waypointLayer,
            this.iconFactory,
            new GpxWaypointDialog(this.iconFactory),
            new GpxMetadataDialog(this.api));

        this.sidebar.setEditEnabled(this.canEditGpx);

        TrailwiseEventBus.gpxEditRequested.on((payload) => {
            if (payload.gpxGuid) {
                //editing a hidden route makes it visible (persisted) so it shows on the map after editing
                const record = this.store.get(payload.gpxGuid);
                if (record && !record.isVisible) {
                    void this.setRouteVisibility(payload.gpxGuid, true);
                }
                void this.editSession.startExisting(payload.gpxGuid);
            } else {
                void this.editSession.startNew();
            }
        });

        //other GPX routes are hidden from the map while editing
        TrailwiseEventBus.mapModeChanged.on((payload) => {
            this.featureLayer.setLayerVisible(payload.to !== 'editing');
            if (payload.to === 'editing') {
                this.waypointPopover.hide();
            }
        });
    }

    /**
     * GPX-first click handling — called by FeatureInfoController before the WMS
     * feature-info flow. A waypoint consumes the click (returns true); a route line
     * reveals itself in the panel but deliberately does NOT consume it, so whatever
     * lies under the route (a right of way, say) can still be queried.
     */
    public handleMapClick = (evt: MapBrowserEvent): boolean => {
        const waypoint = this.waypointLayer.findWaypointAtPixel(evt);
        if (waypoint) {
            this.waypointPopover.show(waypoint);
            return true;
        }
        this.waypointPopover.hide();

        const gpxGuid = this.featureLayer.findRouteAtPixel(evt);
        if (gpxGuid) {
            void this.revealRouteInPanel(gpxGuid);
        }
        return false;
    }

    /**
     * Shows a route's card in the panel. No zoom: the user clicked something already
     * on screen, so moving the map would pull the ground out from under them.
     */
    private revealRouteInPanel = async (gpxGuid: string): Promise<void> => {
        this.openGpxPanel();
        await this.sidebar.revealRoute(gpxGuid);
    }

    /** Current panel filter, read live by the routes + waypoint layers' URL builders. */
    private getFilterCriteria = (): IGpxFilterCriteria => ({
        search: this.panelState.state.search,
        tags: this.panelState.state.tags
    });

    /** The panel's search/tag filter changed — re-request both map layers to match. */
    private onFilterCriteriaChanged = (): void => {
        this.featureLayer.refresh();
        void this.waypointLayer.refresh();
    }

    private async initialiseWaypointTypes(): Promise<void> {
        try {
            const types = await this.api.getWaypointTypes();
            this.iconFactory.setTypes(types);
            this.waypointLayer.olLayer.changed();
        } catch (error) {
            console.error('Failed to load GPX waypoint types', error);
        }
    }

    // -------------------------------------------------------------------------
    // Bootstrap
    // -------------------------------------------------------------------------

    private async initialiseRoutes(): Promise<void> {
        await this.loadExistingRoutes();
        await this.handleHighlight();
    }

    private async loadExistingRoutes(): Promise<void> {
        try {
            const items = await this.api.getSidebarItems();
            for (const item of items) {
                this.applySidebarItem(item);
            }
        } catch (error) {
            console.error('Failed to load GPX route sidebar', error);
        } finally {
            this.store.routesLoaded = true;
            this.sidebar.buildGpxUi();
        }
    }

    /**
     * Merge a sidebar metadata item into the store. Features and extents already
     * in the store (if the /features response beat the sidebar API) are preserved.
     */
    private applySidebarItem(item: IUserGpxRouteSidebarItem): void {
        const colour = normaliseColour(item.colourHex || this.defaultColour, this.defaultColour);
        const sidebarExtent = extentFrom4326SidebarItem(item);
        const existing = this.store.getMutable(item.gpxGuid);
        if (existing) {
            existing.displayName = item.displayName;
            existing.notes = item.notes ?? null;
            existing.colourHex = colour;
            existing.lengthMetres = item.lengthMetres;
            existing.originalFileName = item.originalFileName;
            existing.isVisible = item.isVisible ?? true;
            if (!existing.extent && sidebarExtent) {
                existing.extent = sidebarExtent;
            }
        } else {
            this.store.upsertSilent({
                gpxGuid: item.gpxGuid,
                displayName: item.displayName,
                notes: item.notes ?? null,
                colourHex: colour,
                features: [],
                extent: sidebarExtent,
                lengthMetres: item.lengthMetres,
                originalFileName: item.originalFileName,
                isVisible: item.isVisible ?? true
            });
        }
    }

    // -------------------------------------------------------------------------
    // Route creation callback (from GpxUploadHandler)
    // -------------------------------------------------------------------------

    private onRouteCreated = (
        detail: IUserGpxRouteDetail,
        features: Feature[],
        extent: olExtent.Extent
    ): void => {
        this.addOrUpdateRoute(detail, extent);
        this.zoomToRoute(detail.gpxGuid);
        TrailwiseEventBus.gpxRoutesChanged(detail.gpxGuid);
    }

    /**
     * Merge a full route detail response into the store, then refresh the feature
     * layer so the map geometry reflects the change. The optional `extent` is
     * used immediately (e.g. after upload) so zoomToRoute works before the
     * /features round-trip completes.
     */
    private addOrUpdateRoute(detail: IUserGpxRouteDetail, extent?: olExtent.Extent): void {
        const colour = normaliseColour(detail.colourHex || this.defaultColour, this.defaultColour);
        const isVisible = detail.isVisible ?? true;
        const existing = this.store.getMutable(detail.gpxGuid);

        if (existing) {
            existing.displayName = detail.displayName;
            existing.notes = detail.notes ?? null;
            existing.colourHex = colour;
            existing.lengthMetres = detail.lengthMetres;
            existing.originalFileName = detail.originalFileName;
            existing.isVisible = isVisible;
            if (extent) {
                existing.extent = extent;
            }
        } else {
            this.store.upsertSilent({
                gpxGuid: detail.gpxGuid,
                displayName: detail.displayName,
                notes: detail.notes ?? null,
                colourHex: colour,
                features: [],
                extent: extent ?? null,
                lengthMetres: detail.lengthMetres,
                originalFileName: detail.originalFileName,
                isVisible
            });
        }

        this.featureLayer.refresh();
        this.sidebar.buildGpxUi();
    }

    // -------------------------------------------------------------------------
    // Zoom
    // -------------------------------------------------------------------------

    private zoomToRoute = (gpxGuid: string): void => {
        const record = this.store.get(gpxGuid);
        if (!record) {
            return;
        }
        if (!record.isVisible) {
            void this.setRouteVisibility(gpxGuid, true);
        }
        if (record.extent) {
            this.map.getView().fit(record.extent, {
                nearest: true
            });
        }
    }

    // -------------------------------------------------------------------------
    // Visibility
    // -------------------------------------------------------------------------

    private setRouteVisibility = async (gpxGuid: string, visible: boolean): Promise<void> => {
        const record = this.store.getMutable(gpxGuid);
        if (!record) {
            return;
        }
        const previousVisibility = record.isVisible;
        // Optimistic update
        record.isVisible = visible;
        this.renderVisibilityChange();

        try {
            await this.api.setVisibility(gpxGuid, visible);
        } catch (error) {
            console.error('Failed to update route visibility', error);
            record.isVisible = previousVisibility;
            this.renderVisibilityChange();
        }
    }

    /**
     * Re-renders everything that keys off a route's visibility. Both layers must be
     * invalidated: their style functions read isVisible from the store, but neither
     * re-evaluates until told to, so a missed call leaves stale markers on screen
     * until the next map move.
     */
    private renderVisibilityChange(): void {
        this.featureLayer.invalidateStyles();
        this.waypointLayer.invalidateStyles();
        this.sidebar.buildGpxUi();
    }

    // -------------------------------------------------------------------------
    // Highlight (shared route link)
    // -------------------------------------------------------------------------

    private async handleHighlight(): Promise<void> {
        const highlight = this.highlight;
        if (!highlight) {
            return;
        }
        try {
            if (!this.store.has(highlight.gpxGuid)) {
                console.warn(`Unable to load shared GPX route ${highlight.gpxGuid}`);
                return;
            }
            this.openGpxPanel();
            await this.sidebar.revealRoute(highlight.gpxGuid);
            this.zoomToRoute(highlight.gpxGuid);
            if (highlight.message) {
                this.sidebar.showShareModal(highlight.message);
            }
        } catch (error) {
            console.error('Failed to load shared GPX route', error);
        }
    }

    private resolveHighlightData(): { gpxGuid: string; message: string } | null {
        const dataSource = document.getElementById('mapData');
        if (!dataSource) {
            return null;
        }
        const gpxGuid = dataSource.getAttribute('data-highlight-gpx');
        if (!gpxGuid) {
            return null;
        }
        const message = dataSource.getAttribute('data-highlight-message') ?? '';
        return { gpxGuid, message };
    }

    /** Expands the slide panel on the GPX routes tab (idempotent). */
    private openGpxPanel = (): void => {
        this.slidePanel?.open('gpx');
    }
}
